import { Badge } from '@web-app/components/ui/Badge/badge';
import { cn } from '@web-app/lib/utils';

import { isBridgeActionNeeded, isBridgeCaseShown } from './case-visibility';
import {
  MedicareBridgeCaseView,
  MedicareBridgeChecklistOption,
  MedicareBridgeEligibilityReview,
  MedicareBridgeReviewView,
} from './medicare-bridge.types';
import { medicareBridgeRouteLabel } from './route-label';

/**
 * Candidate status stays distinct from a completed review, and a review
 * stays distinct from a prior-auth approval. "Approved" and "covered"
 * are not used here.
 */
function medicareBridgeBadgeLabel(
  bridgeCase: MedicareBridgeCaseView | null,
): string | null {
  if (!bridgeCase || !isBridgeCaseShown(bridgeCase)) return null;
  if (bridgeCase.identification === 'SUSPECTED') {
    return 'Medicare? Bridge check needed';
  }
  return reviewLabel(
    bridgeCase.eligibilityReview,
    bridgeCase.latestReview,
    bridgeCase.checklist.nextRoutes,
  );
}

function reviewLabel(
  eligibilityReview: MedicareBridgeEligibilityReview,
  latestReview: MedicareBridgeReviewView | null,
  caseRoutes: readonly MedicareBridgeChecklistOption[] | null | undefined,
): string | null {
  switch (eligibilityReview) {
    case 'NOT_STARTED':
      return 'Medicare: Bridge review needed';
    case 'NEEDS_INFORMATION':
      return 'Bridge review: info missing';
    case 'ELIGIBLE':
      return 'Bridge: provider-reviewed eligible';
    case 'NOT_ELIGIBLE': {
      const nextRoute = medicareBridgeRouteLabel(
        latestReview?.nextRoute,
        latestReview?.checklist.nextRoutes,
        caseRoutes,
      );
      return nextRoute
        ? `Bridge: not eligible, ${nextRoute}`
        : 'Bridge: not eligible';
    }
    default:
      return null;
  }
}

export function MedicareBridgeBadge({
  bridgeCase,
  onDark = false,
}: {
  bridgeCase: MedicareBridgeCaseView | null;
  onDark?: boolean;
}) {
  const label = medicareBridgeBadgeLabel(bridgeCase);
  if (!label || !bridgeCase) return null;

  return (
    <Badge
      data-testid="medicare-bridge-badge"
      variant={isBridgeActionNeeded(bridgeCase) ? 'warning' : 'outline'}
      className={cn(onDark && 'border-white/20 bg-white/10 text-white')}
    >
      {label}
    </Badge>
  );
}
